import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { CheckboxRow } from 'components/CheckboxRow/CheckboxRow';
import { CustomCheckbox } from 'components/CustomCheckbox/CustomCheckbox';
import { FormSection } from 'components/FormHelper/FormSection';
import { IndeterminateCheckboxRow } from 'components/IndeterminateCheckboxRow/IndeterminateCheckboxRow';

import { PFLEGENDE_BU_ID_TO_NAME, TENANT_ID } from 'global/constants';

import { getTenantId } from 'utils/tenantUtil';

import './ExpandedConsentSections.sass';

const NETWORK_OPERATORS = [
  'Avacon Netz GmbH',
  'Bayernwerk Netz GmbH',
  'E.DIS Netz GmbH',
  'Schleswig-Holstein Netz GmbH',
  'LEW Verteilnetz GmbH',
  'Mitteldeutsche Netzgesellschaft Strom mbH',
  'Mitteldeutsche Netzgesellschaft Gas mbH',
];

const TENANT_OPERATORS = {
  [TENANT_ID.AVA]: ['Avacon Netz GmbH'],
  [TENANT_ID.EDI]: ['E.DIS Netz GmbH'],
  [TENANT_ID.SHNG]: ['Schleswig-Holstein Netz GmbH'],
  [TENANT_ID.BAG]: ['Bayernwerk Netz GmbH'],
  [TENANT_ID.LVN]: ['LEW Verteilnetz GmbH'],
  [TENANT_ID.MTN]: [
    'Mitteldeutsche Netzgesellschaft Strom mbH',
    'Mitteldeutsche Netzgesellschaft Gas mbH',
  ],
};

const DEFAULT_OPERATOR_SELECTIONS = {
  [TENANT_ID.AVA]: [
    'Bayernwerk Netz GmbH',
    'Mitteldeutsche Netzgesellschaft Strom mbH',
    'Mitteldeutsche Netzgesellschaft Gas mbH',
  ],
};

export const getAvailableNetworkOperators = (tenantId, currentBusinessUnit) => {
  const currentOperator = currentBusinessUnit
    ? PFLEGENDE_BU_ID_TO_NAME[Number(currentBusinessUnit)]
    : undefined;
  const excludedOperators = currentOperator ? [currentOperator.trim()] : TENANT_OPERATORS[tenantId];

  return NETWORK_OPERATORS.filter((operator) => !excludedOperators?.includes(operator));
};

export const getDefaultOperatorSelections = (tenantId) =>
  (DEFAULT_OPERATOR_SELECTIONS[tenantId] || []).filter((operator) =>
    getAvailableNetworkOperators(tenantId).includes(operator)
  );

const TENANT_NAMES = {
  [TENANT_ID.EDI]: 'E.DIS Netz GmbH',
  [TENANT_ID.SHNG]: 'Schleswig-Holstein Netz GmbH',
  [TENANT_ID.AVA]: 'Avacon Netz GmbH',
  [TENANT_ID.BAG]: 'Bayernwerk Netz GmbH',
  [TENANT_ID.LVN]: 'LEW Verteilnetz GmbH',
  [TENANT_ID.MTN]:
    'Mitteldeutsche Netzgesellschaft Strom mbH, die Mitteldeutsche Netzgesellschaft Gas mbH und die Verteilnetz Plauen GmbH',
};

const OperatorCheckboxRow = ({ name, operator, label, checked, onChange }) => {
  const id = `${name}-${operator}`;

  return (
    <div className="checkbox-row expanded-consent-sections__operator-row">
      <FormSection>
        <div className="label-checkbox">
          <CustomCheckbox id={id} name={id} value={checked} onChange={onChange} />
          <label htmlFor={id}>
            {label} {operator}
          </label>
        </div>
      </FormSection>
    </div>
  );
};

OperatorCheckboxRow.propTypes = {
  name: PropTypes.string.isRequired,
  operator: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ConsentPanel = ({
  title,
  name,
  checked,
  description,
  operatorLabel,
  operatorSelections,
  availableOperators,
  onChange,
}) => {
  const selectedVisibleOperators = availableOperators.filter((operator) =>
    operatorSelections.includes(operator)
  );
  const allOperatorsSelected =
    availableOperators.length > 0 && selectedVisibleOperators.length === availableOperators.length;
  const someOperatorsSelected = selectedVisibleOperators.length > 0 && !allOperatorsSelected;

  const toggleOperator = (operator) => {
    const nextOperators = operatorSelections.includes(operator)
      ? operatorSelections.filter((selected) => selected !== operator)
      : [...operatorSelections, operator];

    onChange({
      checked: nextOperators.length > 0,
      operators: nextOperators,
    });
  };

  return (
    <section className="expanded-consent-sections__panel">
      <h3>{title}</h3>
      <CheckboxRow
        name={name}
        value={checked}
        label={description}
        onChange={(event) =>
          onChange({
            checked: event.target.value,
            operators: event.target.value ? operatorSelections : [],
          })
        }
      />
      <IndeterminateCheckboxRow
        name={`${name}-operators`}
        value={allOperatorsSelected}
        indeterminate={someOperatorsSelected}
        label="Ich möchte zusätzlich die Informationen der ausgewählten Netzbetreiber erhalten:"
        onChange={(value) =>
          onChange({
            checked: value,
            operators: value ? availableOperators : [],
          })
        }
      />
      <div className="expanded-consent-sections__operators">
        {availableOperators.map((operator) => (
          <OperatorCheckboxRow
            key={`${name}-${operator}`}
            name={name}
            operator={operator}
            label={operatorLabel}
            checked={operatorSelections.includes(operator)}
            onChange={() => toggleOperator(operator)}
          />
        ))}
      </div>
    </section>
  );
};

ConsentPanel.propTypes = {
  title: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  description: PropTypes.node.isRequired,
  operatorLabel: PropTypes.string.isRequired,
  operatorSelections: PropTypes.arrayOf(PropTypes.string).isRequired,
  availableOperators: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export const ExpandedConsentSections = ({
  values,
  onChange,
  showAddress,
  boxed,
  currentBusinessUnit,
}) => {
  const tenantId = getTenantId();
  const tenantName = TENANT_NAMES[tenantId] || TENANT_NAMES[TENANT_ID.EDI];
  const availableOperators = useMemo(
    () => getAvailableNetworkOperators(tenantId, currentBusinessUnit),
    [tenantId, currentBusinessUnit]
  );

  return (
    <div className={`expanded-consent-sections${boxed ? '' : ' expanded-consent-sections--flat'}`}>
      <ConsentPanel
        title="Informationsversand"
        name="zuInfoversand"
        checked={values.information}
        description={
          <>
            Ich bin / wir sind damit einverstanden, dass die {tenantName} über gesetzliche,
            technische oder organisatorische Neuerungen sowie fachbezogene Veranstaltungen und das
            Installateurportal informiert <br />
            <strong>[Info-Versand per E-Mail]</strong>
          </>
        }
        operatorLabel="Informationsversand"
        operatorSelections={values.informationOperators}
        availableOperators={availableOperators}
        onChange={({ checked, operators }) =>
          onChange({ ...values, information: checked, informationOperators: operators })
        }
      />
      {showAddress && (
        <ConsentPanel
          title="Adressveröffentlichung"
          name="zuAdressveroeff"
          checked={values.address}
          description={
            <>
              Ich bin / wir sind damit einverstanden, dass die {tenantName} meine / unsere im
              Installateurverzeichnis gespeicherten Kontaktdaten (Name, Adresse, Telefonnummer,
              E-Mail-Adresse) in der <strong>Installateur-Suche</strong> für Kunden auf der{' '}
              {tenantName} Homepage und im Netzanschlussportal veröffentlicht
            </>
          }
          operatorLabel="Adressveröffentlichung"
          operatorSelections={values.addressOperators}
          availableOperators={availableOperators}
          onChange={({ checked, operators }) =>
            onChange({ ...values, address: checked, addressOperators: operators })
          }
        />
      )}
    </div>
  );
};

ExpandedConsentSections.propTypes = {
  values: PropTypes.shape({
    information: PropTypes.bool.isRequired,
    address: PropTypes.bool.isRequired,
    informationOperators: PropTypes.arrayOf(PropTypes.string).isRequired,
    addressOperators: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  showAddress: PropTypes.bool,
  boxed: PropTypes.bool,
  currentBusinessUnit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ExpandedConsentSections.defaultProps = {
  showAddress: true,
  boxed: true,
  currentBusinessUnit: undefined,
};
